import React from 'react';
import { CONSTANTS } from '../../data';
import { ASSETS } from '../../assetManifest';
import { CollapsiblePanel } from '../ui/CollapsiblePanel';
import type { AssetsSchema } from '../../assetManifestTypes';

interface ElementsPanelProps {
  discoveredItems: Set<string>;
  isMobile: boolean;
  collapsedPanels: Set<string>;
  onTogglePanel: (panelId: string) => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, element: string) => void;
  onDragEnd: (e: React.DragEvent<HTMLDivElement>) => void;
  onElementDrop: (e: React.DragEvent<HTMLDivElement>, element: string) => void;
  corruptedElements: Set<string>;
}

export const ElementsPanel: React.FC<ElementsPanelProps> = ({
  discoveredItems,
  isMobile,
  collapsedPanels,
  onTogglePanel,
  onDragStart,
  onDragEnd,
  onElementDrop,
  corruptedElements,
}) => {
  const typedAssets = ASSETS as AssetsSchema;
  const antivirusIcon = typedAssets.icons['antivirus'];
  const virusIcon = typedAssets.icons['virus'];
  const purityIcon = typedAssets.icons['purity'];

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  // Map element names to icon keys in the manifest, handling special cases
  const getIconKeyForElement = (element: string): string => {
    const mapping: Record<string, string> = {
      // Special-case aliases
      emotion: 'emotion_wave_heart',
      // Previously mapped digital_immunity -> immunity. We now have a dedicated digital_immunity icon.
      // So no alias is needed here; fall through to the element name itself.
    };
    return mapping[element] ?? element;
  };

  return (
    <div className={`elements-panel ${isMobile ? 'mobile-panel' : 'desktop-panel'} flex flex-col`}>
      <CollapsiblePanel 
        id="base-elements" 
        title={CONSTANTS.PANEL_TITLES.elements}
        isCollapsed={collapsedPanels.has('base-elements')}
        onToggle={onTogglePanel}
      >
        <div className="elements-grid">
          {CONSTANTS.ELEMENTS.map((element) => {
            const iconKey = element === 'emotion' ? 'emotion_wave_heart' : element;
            const iconAsset = typedAssets.icons[iconKey];

            return (
              <div
                key={element}
                className="relative group"
                draggable={true}
                onDragStart={(e) => onDragStart(e, element)}
                onDragEnd={onDragEnd}
              >
                <div
                  data-element={element}
                  className="element mobile-element"
                >
                  {iconAsset ? (
                    <img
                      src={iconAsset.path}
                      alt={element}
                      className="w-full h-full object-contain pointer-events-none"
                    />
                  ) : (
                    <span className="capitalize pointer-events-none text-xs">{element}</span>
                  )}
                </div>
                <div className="element-tooltip">
                  {element}
                </div>
              </div>
            );
          })}
        </div>
      </CollapsiblePanel>

      <CollapsiblePanel 
        id="discovered-elements" 
        title={CONSTANTS.DISCOVERED_UI.sectionTitle}
        isCollapsed={collapsedPanels.has('discovered-elements')}
        onToggle={onTogglePanel}
        className="flex-grow flex flex-col min-h-0"
      >
        <div
          id={CONSTANTS.DISCOVERED_UI.containerId}
          className="elements-grid"
        >
          {Array.from(discoveredItems).sort().map((element) => {
            const isCorrupted = corruptedElements.has(element);
            // Resolve icon for discovered item using reference set
            const iconKey = getIconKeyForElement(element);
            const iconAsset = typedAssets.icons[iconKey];

            return (
              <div
                key={element}
                className="relative group"
                draggable={true}
                onDragStart={(e) => onDragStart(e, element)}
                onDragEnd={onDragEnd}
                onDrop={(e) => onElementDrop(e, element)}
                onDragOver={handleDragOver}
              >
                <div
                  data-element={element}
                  className={`element discovered mobile-element ${isCorrupted ? 'corrupted' : ''}`}
                >
                  {isCorrupted && virusIcon ? (
                    <img src={virusIcon.path} alt="virus" className="w-full h-full object-contain pointer-events-none" />
                  ) : iconAsset ? (
                    <img src={iconAsset.path} alt={element} className="w-full h-full object-contain pointer-events-none" />
                  ) : element === 'antivirus' && antivirusIcon ? (
                    <img src={antivirusIcon.path} alt="antivirus" className="w-full h-full object-contain pointer-events-none" />
                  ) : element === 'purity' && purityIcon ? (
                    <img src={purityIcon.path} alt="purity" className="w-full h-full object-contain pointer-events-none" />
                  ) : (
                    <span className="capitalize pointer-events-none text-xs">{element}</span>
                  )}
                </div>
                <div className="element-tooltip">
                  {isCorrupted ? `CORRUPTED: ${element}` : element}
                </div>
              </div>
            );
          })}
        </div>
      </CollapsiblePanel>
    </div>
  );
};
